/**
 * Shipping Calculator Enhancement
 *
 * This script enhances the cart page by automatically calculating and displaying shipping costs.
 * Features:
 * - Shows estimated shipping cost based on detected or specified location
 * - Updates totals to include shipping
 * - Works with AJAX cart updates
 * - Persists shipping rates across page loads using localStorage
 */

(function() {
  'use strict';

  const safeConsole = Object.freeze({
    log: function() {
      if (window.console && typeof window.console.log === 'function') {
        window.console.log.apply(window.console, arguments);
      }
    },
    error: function() {
      if (window.console && typeof window.console.error === 'function') {
        window.console.error.apply(window.console, arguments);
      }
    }
  });

  // Configuration
  const config = {
    selectors: {
      // Price elements
      cartSubtotal: '[data-cart-subtotal]',
      cartShipping: '[data-cart-shipping]',
      cartTotal: '[data-cart-total]',
      cartTotalWithShipping: '[data-cart-total-with-shipping]',
      
      // UI elements
      miniToggle: '[data-cartshipping-toggle-mini]',
      
      // Title elements
      cartTitleTotal: '[data-cart-title-total]',
      cartTitleBreakdown: '[data-cart-title-breakdown]',
      cartTitleSmall: '.cart-title-total--small',
      cartTitleLarge: '.cart-title-total--large'
    },
    address: {
      country: 'United States',
      province: 'CA',    // California
      zip: '90210'       // Beverly Hills (valid ZIP needed for accurate rates)
    },
    debug: false // Enable debug logging
  };

  // State management
  const state = {
    elements: {},          // DOM elements
    cart: null,            // Current cart data
    settings: null,        // Theme settings
    lastShippingCost: null,   // Last calculated shipping cost (null = not calculated)
    cartHash: '',          // Hash to check if cart changed
    initialized: false,    // Whether initialization is complete
    customerAddress: null  // Customer address if available
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  /**
   * Initialize the shipping calculator enhancement
   */
  function initialize() {
    // Get cart data
    const sectionDataElement = document.querySelector('[data-section-type="static-cart"][data-section-data]');
    if (!sectionDataElement) return;

    try {
      // Parse cart data
      const sectionData = JSON.parse(sectionDataElement.textContent || '{}');
      state.cart = sectionData.cart || {};
      state.settings = sectionData.settings || {};
      
      // Check if auto-shipping is enabled
      if (!state.settings.auto_shipping) return;
      
      // Check if shipping is required
      if (!state.cart || !state.cart.requires_shipping) return;
      
      // Log debug info
      debugLog('Initializing shipping calculator enhancement');
      
      // Setup the theme API listener
      setupThemeListener();
      
      // Setup mini toggle click handler
      const miniToggle = document.querySelector(config.selectors.miniToggle);
      if (miniToggle) {
        miniToggle.addEventListener('click', handleMiniToggleClick);
      }
      
      // Add resize listener for title elements
      window.addEventListener('resize', debounce(ensureTitleElements, 250));
      
      // Remove any duplicate breakdown elements that might exist in the DOM initially
      const breakdownElements = document.querySelectorAll(config.selectors.cartTitleBreakdown);
      if (breakdownElements.length > 1) {
        // Keep only the first one, remove the rest
        Array.from(breakdownElements).slice(1).forEach(el => {
          if (el.parentNode) {
            el.parentNode.removeChild(el);
          }
        });
      }
      
      // Ensure title elements are correctly positioned on load
      ensureTitleElements();
      
      // Get customer address
      checkCustomerAddress();
      
      // Initial shipping calculation
      if (state.lastShippingCost !== null) {
        updateUI(state.lastShippingCost, state.cart.total_price);
      } else {
        calculateShipping();
      }
      
      state.initialized = true;
    } catch (error) {
      safeConsole.error('Error initializing shipping calculator:', error);
    }
  }

  /**
   * Add CSS styles to prevent layout shift during initialization
   */
  function addLayoutStyles() {
    // This function has been removed in favor of using CSS in the Liquid template
  }

  /**
   * Check for a customer address
   * First check for customer data from Shopify, then try geolocation if not available
   */
  function checkCustomerAddress() {
    try {
      debugLog('Checking for customer address');
      
      // First try to get address data from the section data
      if (state.settings.customer_address) {
        // If Shopify has provided customer address data through the section data
        debugLog('Found customer address data from Shopify', state.settings.customer_address);
        state.customerAddress = state.settings.customer_address;
        
        // Update config address with customer data
        if (state.customerAddress.country) {
          config.address.country = state.customerAddress.country;
        }
        
        if (state.customerAddress.province) {
          config.address.province = state.customerAddress.province;
        }
        
        if (state.customerAddress.zip) {
          config.address.zip = state.customerAddress.zip;
        }
        
        debugLog('Using customer address from Shopify for shipping calculation', config.address);
        return;
      }
      
      // If no customer address is available, try to get it from IP
      debugLog('No customer address found in Shopify data, attempting IP geolocation');
      getAddressFromIP();
    } catch (error) {
      safeConsole.error('Error checking for customer address:', error);
    }
  }

  /**
   * Get address data from IP geolocation
   */
  function getAddressFromIP() {
    try {
      // Use ipinfo.io to get location data
      debugLog('Fetching location data from IP address');
      
      fetch('https://ipinfo.io/json')
        .then(response => {
          if (!response.ok) {
            throw new Error(`Error ${response.status}: ${response.statusText}`);
          }
          return response.json();
        })
        .then(data => {
          debugLog('IP location data received', data);
          
          if (data && data.country) {
            // Map country code to full name if needed
            const countryCode = data.country;
            let country = countryCode;
            
            // Simple mapping for common countries - in production, would use a complete list
            const countryMap = {
              'US': 'United States',
              'CA': 'Canada', 
              'GB': 'United Kingdom',
              'AU': 'Australia'
            };
            
            if (countryMap[countryCode]) {
              country = countryMap[countryCode];
            }
            
            // Update config address with IP data
            config.address.country = country;
            
            if (data.region) {
              config.address.province = data.region;
            }
            
            if (data.postal) {
              config.address.zip = data.postal;
            }
            
            debugLog('Using IP-based location for shipping calculation', config.address);
            
            // Recalculate shipping with the new address
            calculateShipping();
          }
        })
        .catch(error => {
          safeConsole.error('Error getting location from IP:', error);
          debugLog('Failed to get location from IP, using default address');
        });
    } catch (error) {
      safeConsole.error('Error in IP geolocation:', error);
    }
  }

  /**
   * Set up listener for theme API cart updates
   */
  function setupThemeListener() {
    if (window.api && window.api.theme) {
      // Listen for cart:total updates from StaticCart
      window.api.theme.useValue({
        name: 'cart:total',
        handler: function(data) {
          refreshCart();
          return data;
        }
      });
    }
  }

  /**
   * Handle mini toggle click
   */
  function handleMiniToggleClick() {
    // Find shipping calculator elements
    const shippingToggle = document.querySelector('[data-cartshipping-toggle]');
    const shipping = document.querySelector('[data-cartshipping]');
    
    if (shipping && shippingToggle) {
      // Toggle open/closed state
      if (!shipping.classList.contains('open')) {
        shippingToggle.click();
      }
      
      // Scroll to the shipping calculator
      setTimeout(() => {
        window.scrollTo({
          top: shipping.getBoundingClientRect().top + window.pageYOffset - 220,
          behavior: 'smooth'
        });
      }, 100);
    } else {
      // If no calculator UI, just refresh rates
      refreshCart();
    }
  }

  /**
   * Fetch latest cart data and recalculate shipping
   */
  function refreshCart() {
    // Show calculating state
    updateLoadingState(true);
    
    fetch('/cart.js')
      .then(response => response.json())
      .then(data => {
        // Update state with new cart data
        state.cart = data;
        
        // Check if cart contents changed
        const newCartHash = generateCartHash(data);
        const cartChanged = newCartHash !== state.cartHash;
        
        if (cartChanged) {
          state.cartHash = newCartHash;
          calculateShipping();
        } else {
          // Use saved shipping rate if available
          if (state.lastShippingCost !== null) {
            updateUI(state.lastShippingCost, state.cart.total_price);
          } else {
            calculateShipping();
          }
        }
      })
      .catch(error => {
        safeConsole.error('Error fetching cart data:', error);
        updateLoadingState(false);
      });
  }

  /**
   * Calculate shipping rates for the cart
   */
  function calculateShipping() {
    // Show calculating state
    updateLoadingState(true);
    
    // Format address for API request
    const address = config.address;
    const queryString = Object.keys(address)
      .map(key => `shipping_address[${key}]=${encodeURIComponent(address[key])}`)
      .join('&');
    
    debugLog('Calculating shipping with address', address);
    
    // Fetch shipping rates from Shopify API
    fetch(`/cart/shipping_rates.json?${queryString}`)
      .then(response => {
        if (!response.ok) {
          return response.text().then(text => {
            debugLog('Shipping rates fetch failed', {status: response.status, text});
            throw new Error(`Error ${response.status}: ${text}`);
          });
        }
        return response.json();
      })
      .then(data => {
        debugLog('Shipping rates API response', data);
        const rates = data.shipping_rates || [];
        debugLog('Parsed rates array', rates);
        if (rates.length > 0) {
          // Use first rate (usually cheapest)
          const rate = rates[0];
          const price = parseFloat(rate.price) * 100; // Convert to cents
          
          debugLog(`Selected shipping rate: ${rate.name} - ${formatMoney(price)}`);

          // Update UI with shipping cost
          state.lastShippingCost = price;
          debugLog('Calling updateUI with shippingCost', {shippingCost: state.lastShippingCost, subtotal: state.cart.total_price});
          updateUI(state.lastShippingCost, state.cart.total_price);
          
          // Update the cart total through the theme API
          if (window.api && window.api.theme && typeof window.api.theme.updateValue === 'function') {
            try {
              window.api.theme.updateValue('cart:total', {
                price: state.cart.total_price + state.lastShippingCost,
                compare_at_price: state.cart.total_price + state.lastShippingCost
              });
            } catch (e) {
              safeConsole.error('Error updating cart:total:', e);
            }
          }
        } else {
          debugLog('No shipping rates returned', rates);
          // Don't reset lastShippingCost if we already have a value
          if (state.lastShippingCost === null) {
            debugLog('Calling updateUI with shippingCost 0', {subtotal: state.cart.total_price});
            updateUI(0, state.cart.total_price);
          } else {
            debugLog('Calling updateUI with previous lastShippingCost', {shippingCost: state.lastShippingCost, subtotal: state.cart.total_price});
            updateUI(state.lastShippingCost, state.cart.total_price);
          }
        }
      })
      .catch(error => {
        safeConsole.error('Error calculating shipping:', error);
        debugLog('Error in shipping calculation, using cached value if available', {lastShippingCost: state.lastShippingCost});
        updateLoadingState(false);

        // Use cached value if available
        if (state.lastShippingCost !== null) {
          updateUI(state.lastShippingCost, state.cart.total_price);
        }
      });
  }

  /**
   * Whether the cart contains a freight-quote product.
   * These carry a "Shipping Quote" line item property added on the product page
   * when the customer opts to be quoted after purchase, so their shipping cost
   * is not known until we quote it.
   */
  function cartHasShippingQuote() {
    return Boolean(
      state.cart &&
      Array.isArray(state.cart.items) &&
      state.cart.items.some(item => item.properties && item.properties['Shipping Quote'])
    );
  }

  /**
   * Update the UI with shipping and total costs
   */
  function updateUI(shippingCost, subtotal) {
    try {
      debugLog('updateUI called', {shippingCost, subtotal});
      // Calculate total
      const total = subtotal + shippingCost;
      
      // Update shipping cost display
      // Freight-quote products have no known shipping cost until we quote them,
      // so show TBD instead of a free/calculated rate when one is in the cart.
      const requiresShippingQuote = cartHasShippingQuote();
      const shippingElements = document.querySelectorAll(config.selectors.cartShipping);
      debugLog('Found shippingElements', shippingElements.length);
      shippingElements.forEach(el => {
        if (requiresShippingQuote) {
          el.textContent = 'TBD';
          el.setAttribute('aria-label', 'Shipping to be determined');
        } else if (shippingCost === 0) {
          el.textContent = 'FREE';
          el.setAttribute('aria-label', 'Free shipping');
        } else if (shippingCost > 0) {
          el.textContent = formatMoney(shippingCost);
          el.removeAttribute('aria-label');
        } else {
          el.textContent = '—';
          el.removeAttribute('aria-label');
        }
        debugLog('Set shipping element textContent', el.textContent);
      });

      // Update subtotal displays
      const subtotalElements = document.querySelectorAll(config.selectors.cartSubtotal);
      subtotalElements.forEach(el => {
        el.textContent = formatMoney(subtotal);
      });
      
      // Update total with shipping
      const totalWithShippingElements = document.querySelectorAll(config.selectors.cartTotalWithShipping);
      totalWithShippingElements.forEach(el => {
        el.textContent = formatMoney(total);
      });

      // Update other cart total displays (excluding the title breakdown)
      const totalElements = document.querySelectorAll(config.selectors.cartTotal);
      totalElements.forEach(el => {
        // Skip elements inside the cart title that we've already updated
        if (el.closest(config.selectors.cartTitleTotal)) {
          return;
        }
        
        if (el.tagName === 'SPAN' || el.tagName === 'DIV') {
          el.textContent = formatMoney(total);
        }
        if (el.hasAttribute('data-cart-total')) {
          el.setAttribute('data-cart-total', total);
        }
      });

      // Handle mobile/desktop breakpoint for title totals if needed
      ensureTitleElements();
    } catch (error) {
      safeConsole.error('Error updating UI:', error);
    }

    // Remove loading state
    debugLog('updateUI finished, calling updateLoadingState(false)');
    updateLoadingState(false);
  }

  /**
   * Ensure title elements are in the right containers based on viewport width
   */
  function ensureTitleElements() {
    // Check for both title containers
    const titleSmallContainer = document.querySelector(config.selectors.cartTitleSmall);
    const titleLargeContainer = document.querySelector(config.selectors.cartTitleLarge);
    
    if (!titleSmallContainer || !titleLargeContainer) return;
    
    // Find breakdown element
    const titleBreakdown = document.querySelector(config.selectors.cartTitleBreakdown);
    if (!titleBreakdown) return;
    
    // Determine the right container based on viewport width
    const isMobile = window.innerWidth < 480;
    const currentParent = titleBreakdown.parentNode;
    
    if (isMobile) {
      // On mobile, move to small container if not already there
      if (!titleSmallContainer.contains(titleBreakdown)) {
        currentParent.removeChild(titleBreakdown);
        titleSmallContainer.appendChild(titleBreakdown);
      }
    } else {
      // On desktop, move to large container if not already there
      if (!titleLargeContainer.contains(titleBreakdown)) {
        currentParent.removeChild(titleBreakdown);
        titleLargeContainer.appendChild(titleBreakdown);
      }
    }
  }

  /**
   * Show/hide loading state
   */
  function updateLoadingState(isLoading) {
    const shippingElements = document.querySelectorAll(config.selectors.cartShipping);
    
    shippingElements.forEach(el => {
      if (isLoading) {
        el.textContent = 'Calculating...';
      }
    });
  }

  /**
   * Debug logger that only logs if debug is enabled
   */
  function debugLog(message, data) {
    if (config.debug) {
      if (data) {
        safeConsole.log(`[ShippingCalc] ${message}:`, data);
      } else {
        safeConsole.log(`[ShippingCalc] ${message}`);
      }
    }
  }

  /**
   * Generate a simple hash of the cart contents
   */
  function generateCartHash(cartData) {
    if (!cartData || !cartData.items || !cartData.items.length) {
      return '';
    }
    
    return cartData.items.map(item => {
      return `${item.variant_id}:${item.quantity}`;
    }).join('|');
  }

  /**
   * Format money according to store settings
   */
  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, state.settings?.money_format);
    }
    
    // Simple fallback
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Simple debounce function for resize events
   */
  function debounce(func, wait) {
    let timeout;
    return function() {
      const context = this;
      const args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        func.apply(context, args);
      }, wait);
    };
  }
})();
